import React from 'react';
import { clsx } from 'clsx';
import {
  DialogContent,
  DialogContentProps,
  DialogOverlay,
  DialogOverlayProps
} from '@reach/dialog';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import { color as colors } from '@strava/ui-tokens/js';

import { Button } from '../Button/Button';

import './ReachUI.css';
import styles from './Modal.module.scss';

//
// ModalOverlay
//
// Reach UI docs: https://reach.tech/dialog/#dialogoverlay
// --------------------

export interface ModalOverlayProps
  extends DialogOverlayProps,
    React.ComponentPropsWithoutRef<'div'> {
  className?: string;
}

export const ModalOverlay = React.forwardRef<HTMLDivElement, ModalOverlayProps>(
  ({ className, children, ...otherProps }, ref) => {
    return (
      <DialogOverlay
        ref={ref}
        className={clsx(styles.overlay, className)}
        {...otherProps}
      >
        {children}
      </DialogOverlay>
    );
  }
);

//
// ModalContent
//
// Reach UI docs: https://reach.tech/dialog/#dialogcontent
// --------------------

export interface ModalContentProps
  extends DialogContentProps,
    React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const ModalContent = ({
  className,
  children,
  ...otherProps
}: ModalContentProps) => {
  return (
    <DialogContent className={clsx(styles.dialog, className)} {...otherProps}>
      {children}
    </DialogContent>
  );
};

//
// ModalTitle
// --------------------

export interface ModalTitleProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  className?: string;
}

export const ModalTitle = ({
  className,
  children,
  ...otherProps
}: ModalTitleProps) => {
  return (
    <h2 className={clsx(styles.title, className)} {...otherProps}>
      {children}
    </h2>
  );
};

//
// ModalActions
// --------------------

export interface ModalActionsProps
  extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  className?: string;
}

export const ModalActions = ({
  className,
  children,
  ...otherProps
}: ModalActionsProps) => {
  return (
    <div className={clsx(styles.actions, className)} {...otherProps}>
      {children}
    </div>
  );
};

//
// ModalHeroImage
// --------------------

export interface ModalHeroImageProps
  extends React.ComponentPropsWithoutRef<'img'> {
  children?: React.ReactNode;
  className?: string;
}

export const ModalHeroImage = ({
  className,
  src,
  alt,
  ...otherProps
}: ModalHeroImageProps) => {
  return (
    <img
      src={src}
      alt={alt}
      className={clsx(styles.heroImage, className)}
      {...otherProps}
    />
  );
};

//
// ModalBody
// --------------------

export interface ModalBodyProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;
  className?: string;
}

export const ModalBody = ({ className, ...otherProps }: ModalBodyProps) => {
  return <div className={clsx(styles.body, className)} {...otherProps} />;
};

//
// ModalCloseButton
// --------------------

export interface ModalCloseButtonProps
  extends React.ComponentPropsWithoutRef<'button'> {
  className?: string;
  color?: string;
}

export const ModalCloseButton = ({
  className,
  color,
  ...otherProps
}: ModalCloseButtonProps) => {
  return (
    <Button type="button" variant="icon" className={className} {...otherProps}>
      <ActionsCancelNormalXsmall fill={color ?? colors.colorCoreAsphalt} />
    </Button>
  );
};

//
// Modal
// --------------------

export interface ModalProps extends React.ComponentPropsWithoutRef<'div'> {
  className?: string;
  isOpen?: boolean;
  onDismiss?: () => void;
  hasClose?: boolean;
  children: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialFocusRef?: React.RefObject<any>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const Modal = ({
  className,
  isOpen = false,
  onDismiss,
  hasClose,
  children,
  initialFocusRef,
  style,
  ...otherProps
}: ModalProps) => {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onDismiss={onDismiss}
      initialFocusRef={initialFocusRef}
      style={style}
    >
      <ModalContent className={className} {...otherProps}>
        {hasClose && (
          <ModalCloseButton
            className={styles.closeButton}
            onClick={onDismiss}
            data-testid="modal-close-button"
          />
        )}
        {children}
      </ModalContent>
    </ModalOverlay>
  );
};

// Adding support to use the compound component pattern and have backwards
// compatibility with the existing pattern. To learn more read,
// https://www.smashingmagazine.com/2021/08/compound-components-react/
Modal.Overlay = ModalOverlay;
Modal.Content = ModalContent;
Modal.Title = ModalTitle;
Modal.Actions = ModalActions;
Modal.HeroImage = ModalHeroImage;
Modal.Body = ModalBody;
Modal.CloseButton = ModalCloseButton;
